import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, List, ListItemButton,
  ListItemText, MenuItem, Skeleton, Snackbar, Stack, TextField, Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { BsPencil, BsPlusLg, BsTrash } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import CorrectTextModal from './CorrectTextModal';
import Fiche from '../Expert/Fiche';
import { themeExpert } from '../../theme/expert';
import ChampDate from '../ChampDate/ChampDate';

const TYPES = [
  { code: 'description', libelle: 'Texte' },
  { code: 'at_date', libelle: 'Date' },
  { code: 'check_box', libelle: 'Oui / non' },
];

const VIDE = { title: '', type_field: 'description', description: '',
               at_date: '', check_box: false };

const OUTILS_QUILL = {
  toolbar: [
    [{ header: '1' }, { header: '2' }, { font: [] }],
    [{ size: [] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' },
     { indent: '-1' }, { indent: '+1' }],
    ['clean'],
  ],
};

const FORMATS_QUILL = [
  'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
  'blockquote', 'list', 'bullet', 'indent',
];

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : null);

/** Le rapport, champ par champ. C'est la sortie du dossier. */
function ReportView() {
  const { expertiseId } = useParams();

  const [champs, setChamps] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');

  const [fiche, setFiche] = useState(null);
  const [choixModele, setChoixModele] = useState(false);
  const [aCorriger, setACorriger] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get('/reportfieldtexts/',
                                               { params: { expertise_id: expertiseId } });
      setChamps(data.results || data);
      setErreur('');
    } catch {
      setErreur("Le rapport n'a pas pu être chargé.");
    } finally {
      setChargement(false);
    }
  }, [expertiseId]);

  useEffect(() => { charger(); }, [charger]);

  const ouvrirModeles = async () => {
    try {
      const { data } = await axiosInstance.get('/report_model/');
      setModeles(data.results || data);
      setChoixModele(true);
    } catch {
      setErreur("Les modèles n'ont pas pu être chargés.");
    }
  };

  const appliquerModele = async (modele) => {
    setOccupe(true);
    try {
      await axiosInstance.post('/report/add_from_template/', {
        expertise_id: expertiseId, template_id: modele.id,
      });
      setChoixModele(false);
      setMessage('Modèle appliqué');
      charger();
    } catch (error) {
      setErreur(error.response?.data?.detail
        || "Le modèle n'a pas pu être appliqué.");
    } finally {
      setOccupe(false);
    }
  };

  const enregistrer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      const corps = { ...fiche, at_date: fiche.at_date || null,
                      expertise: expertiseId };
      if (fiche.id) {
        await axiosInstance.put(`/reportfieldtexts/${fiche.id}/`, corps);
      } else {
        await axiosInstance.post('/reportfieldtexts/', corps,
                                 { params: { expertise_id: expertiseId } });
      }
      setFiche(null);
      setMessage('Champ enregistré');
      charger();
    } catch {
      setErreur("Le champ n'a pas pu être enregistré.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/reportfieldtexts/${aSupprimer.id}/`);
      setASupprimer(null);
      setMessage('Champ retiré');
      charger();
    } catch {
      setErreur("Le champ n'a pas pu être retiré.");
    } finally {
      setOccupe(false);
    }
  };

  const ouvrir = (champ) => setFiche(champ ? {
    ...VIDE, ...champ,
    description: champ.description || '',
    at_date: (champ.at_date || '').split('T')[0],
    check_box: Boolean(champ.check_box),
  } : { ...VIDE });

  const contenu = (champ) => {
    if (champ.type_field === 'check_box') {
      return <Chip size="small" color={champ.check_box ? 'success' : 'default'}
                   label={champ.check_box ? 'Oui' : 'Non'} />;
    }
    if (champ.type_field === 'at_date') {
      return (
        <Typography variant="body2">
          {dateCourte(champ.at_date) || (
            <Box component="span" sx={{ color: 'text.disabled' }}>
              Aucune date
            </Box>
          )}
        </Typography>
      );
    }
    if (!champ.description) {
      return (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          Ce champ est vide.
        </Typography>
      );
    }
    return <Box className="texte-riche" sx={{ '& img': { maxWidth: '100%' },
                      '& p:last-child': { mb: 0 } }}
                dangerouslySetInnerHTML={{ __html: champ.description }} />;
  };

  return (
    <>
      <Stack spacing={3}>
        {erreur && <Alert severity="error"
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        <Fiche
          titre="Rapport"
          explication="Ce que vous remettez. Chaque champ y devient un paragraphe."
          actions={(
            <Stack direction="row" spacing={1.5}>
              <Button size="small" variant="outlined" onClick={ouvrirModeles}>
                Partir d'un modèle
              </Button>
              <Button size="small" variant="contained"
                      startIcon={<BsPlusLg />} onClick={() => ouvrir(null)}>
                Ajouter un champ
              </Button>
            </Stack>
          )}
        >
          {chargement && <Stack spacing={1}>
            {[...Array(4)].map((_, i) => <Skeleton key={i} height={48} />)}
          </Stack>}

          {!chargement && champs.length === 0 && (
            <Typography variant="body2" color="text.secondary"
                        sx={{ py: 3, textAlign: 'center' }}>
              Le rapport est vide. Partez d'un modèle, ou ajoutez les champs
              un à un.
            </Typography>
          )}

          <Stack divider={<Box sx={{ borderTop: '1px solid',
                                     borderColor: 'divider' }} />}>
            {champs.map((champ) => (
              <Box key={champ.id} sx={{ py: 2 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start"
                       justifyContent="space-between">
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="h3" sx={{ mb: 0.5 }}>
                      {champ.title || 'Sans titre'}
                    </Typography>
                    {contenu(champ)}
                  </Box>
                  <Stack direction="row" sx={{ flexShrink: 0 }}>
                    {champ.type_field === 'description' && (
                      <Tooltip title="Faire relire par l'IA">
                        <span>
                          <Button size="small" variant="outlined"
                                  onClick={() => setACorriger(champ)}
                                  disabled={!champ.description}
                                  sx={{ mr: 1 }}>
                            Corriger
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Modifier">
                      <IconButton size="small" onClick={() => ouvrir(champ)}>
                        <BsPencil />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Retirer">
                      <IconButton size="small"
                                  onClick={() => setASupprimer(champ)}>
                        <BsTrash />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Fiche>
      </Stack>

      <CorrectTextModal
        show={Boolean(aCorriger)}
        onHide={() => setACorriger(null)}
        correctUrl={aCorriger ? `/reportfieldtexts/${aCorriger.id}/correct/` : ''}
        originalText={aCorriger?.description || ''}
        onSave={async (html) => {
          await axiosInstance.put(`/reportfieldtexts/${aCorriger.id}/`,
                                  { ...aCorriger, description: html });
          setACorriger(null);
          setMessage('Correction enregistrée');
          charger();
        }}
      />

      <ThemeProvider theme={themeExpert}>
        <Dialog open={fiche !== null} onClose={() => setFiche(null)}
                fullWidth maxWidth="md">
          <Box component="form" onSubmit={enregistrer} noValidate>
            <DialogTitle>
              {fiche?.id ? 'Modifier le champ' : 'Ajouter un champ'}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="Titre" value={fiche?.title || ''}
                             onChange={(e) => setFiche(
                               (s) => ({ ...s, title: e.target.value }))}
                             required autoFocus />
                  <TextField select label="Type"
                             value={fiche?.type_field || 'description'}
                             onChange={(e) => setFiche(
                               (s) => ({ ...s, type_field: e.target.value }))}
                             sx={{ minWidth: 180 }}>
                    {TYPES.map(({ code, libelle }) => (
                      <MenuItem key={code} value={code}>{libelle}</MenuItem>
                    ))}
                  </TextField>
                </Stack>

                {/* Le contenu se saisit ici, à la création comme à la
                    modification : il fallait auparavant créer le champ, puis
                    le rouvrir pour le remplir. */}
                {fiche?.type_field === 'description' && (
                  <Box sx={{ '& .ql-container': { minHeight: 300 },
                             '& .ql-editor': { minHeight: 300 } }}>
                    <ReactQuill
                      theme="snow"
                      value={fiche.description || ''}
                      onChange={(html) => setFiche(
                        (s) => ({ ...s, description: html }))}
                      modules={OUTILS_QUILL}
                      formats={FORMATS_QUILL}
                      placeholder="Votre texte…"
                    />
                  </Box>
                )}

                {fiche?.type_field === 'at_date' && (
                  <ChampDate label="Date"
                             value={fiche.at_date || ''}
                             onChange={(e) => setFiche(
                               (s) => ({ ...s, at_date: e.target.value }))}
                             InputLabelProps={{ shrink: true }}
                             sx={{ maxWidth: 220 }} />
                )}

                {fiche?.type_field === 'check_box' && (
                  <FormControlLabel
                    control={<Checkbox checked={Boolean(fiche.check_box)}
                                       onChange={(e) => setFiche(
                                         (s) => ({ ...s, check_box: e.target.checked }))} />}
                    label="Oui"
                  />
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setFiche(null)} disabled={occupe}>
                Annuler
              </Button>
              <Button type="submit" variant="contained"
                      disabled={occupe || !fiche?.title?.trim()}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={choixModele} onClose={() => setChoixModele(false)}
                fullWidth maxWidth="sm">
          <DialogTitle>Partir d'un modèle</DialogTitle>
          <DialogContent dividers>
            {modeles.length === 0 ? (
              <Alert severity="info">Aucun modèle de rapport disponible.</Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary"
                            sx={{ mb: 2 }}>
                  Les champs du modèle s'ajoutent à ceux déjà présents ; rien
                  n'est remplacé.
                </Typography>
                <List disablePadding>
                  {modeles.map((modele) => (
                    <ListItemButton key={modele.id} disabled={occupe}
                                    onClick={() => appliquerModele(modele)}>
                      <ListItemText primary={modele.name} />
                    </ListItemButton>
                  ))}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setChoixModele(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Retirer ce champ du rapport ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer?.title || 'Sans titre'} » et son contenu seront
              perdus. C'est définitif.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Retirer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default ReportView;
